import classNames from 'classnames'
import type { ReactElement } from 'react'
import {
  cloneElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import type {
  Placement,
  ReferenceType,
  UseHoverProps,
} from '@floating-ui/react'
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useInteractions,
  FloatingPortal,
  useTransitionStyles,
  arrow,
  FloatingArrow,
  useRole,
  useClick,
  FloatingFocusManager,
} from '@floating-ui/react'

const theme = {
  tooltip: {
    base: {
      className:
        'bg-on-surface-var text-xs text-core-tertiary p-2 rounded-lg cursor-default relative z-[9999] min-w-[30px] max-w-[300px] text-center',
    },
    transition: {
      duration: 300,
    },
  },
}

// eslint-disable-next-line react-refresh/only-export-components
export function hideAllTooltips() {
  const event = new KeyboardEvent('keydown', {
    key: 'Escape',
  })
  document.dispatchEvent(event)
}

export type BreakoutTooltipProps = {
  enabled?: boolean
  cloneRef?: (ref: ReferenceType) => void
  className?: string
  content:
    | ReactElement
    | string
    | (({ close }: { close: () => void }) => ReactElement | string)
  children: ReactElement | string
  placement?: Placement
  tooltipClassNameOverrides?: string
  delay?: UseHoverProps['delay']
  // Whether to render the tooltip in a portal or not - defaults to true
  portal?: boolean
  portalId?: string
  ariaHidden?: boolean
  forceOpen?: boolean
  transitionDuration?:
    | number
    | {
        open?: number
        close?: number
      }
  treatReactElementAsString?: boolean
  /** The 'popover' behavior shows the content on click and keeps it open after the mouse moves away. */
  behavior?: 'tooltip' | 'popover'
  /** ARIA role for the floating element. Use 'alert' for urgent notifications that screen readers should announce immediately. */
  role?: 'tooltip' | 'alert'
}

export function BreakoutTooltip({
  content,
  children,
  cloneRef,
  placement,
  delay,
  portal = true,
  portalId,
  className,
  transitionDuration,
  tooltipClassNameOverrides,
  enabled = true,
  forceOpen = false,
  ariaHidden = false,
  treatReactElementAsString = false,
  behavior = 'tooltip',
  role: roleProp = 'tooltip',
}: BreakoutTooltipProps) {
  const [isOpen, setIsOpen] = useState(false)
  const tooltipPlacement = placement || 'bottom'

  const arrowRef = useRef(null)

  const id = useId()
  const { refs, floatingStyles, context } = useFloating({
    open: forceOpen || isOpen,
    onOpenChange: setIsOpen,
    placement: tooltipPlacement,
    // Make sure the tooltip stays on the screen
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(10),
      flip({
        fallbackAxisSideDirection: 'start',
      }),
      shift(),
      arrow({
        element: arrowRef,
      }),
    ],
  })

  const role = useRole(context, { role: 'tooltip' })

  useEffect(() => {
    // If the tooltip is disabled, reset the open state to closed
    // That prevents it from showing up open when it's enabled again
    if (enabled === false) setIsOpen(false)
  }, [enabled])

  const { isMounted, styles } = useTransitionStyles(context, {
    // Configure both open and close durations:
    duration:
      behavior === 'popover'
        ? 0
        : (transitionDuration ?? theme.tooltip.transition.duration),
  })

  // Event listeners to change the open state
  const hover = useHover(context, {
    move: false,
    delay,
    enabled: behavior === 'tooltip',
  })
  const focus = useFocus(context, {
    enabled: behavior === 'tooltip',
  })
  const click = useClick(context, {
    enabled: behavior === 'popover',
  })
  const dismiss = useDismiss(context, {
    ancestorScroll: true,
  })

  // Merge all the interactions into prop getters
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    click,
    dismiss,
    role,
  ])

  const tooltipClassName = theme.tooltip.base.className

  const close = useCallback(() => setIsOpen(false), [])

  const resolvedContent =
    typeof content === 'function' ? content({ close }) : content

  let contentIsReactElement = typeof resolvedContent !== 'string'

  if (treatReactElementAsString && contentIsReactElement) {
    contentIsReactElement = false
  }

  const floatingContent = (
    <div
      aria-hidden={ariaHidden}
      id={id}
      className={classNames(
        className,
        {
          [tooltipClassName]: !contentIsReactElement,
          'relative z-[9999] cursor-default': contentIsReactElement,
        },
        tooltipClassNameOverrides
      )}
      ref={refs.setFloating}
      style={{ ...floatingStyles, ...styles }}
      {...getFloatingProps()}
      role={roleProp}
    >
      {!contentIsReactElement && (
        <FloatingArrow
          className="fill-on-surface-var"
          ref={arrowRef}
          context={context}
        />
      )}
      {resolvedContent}
    </div>
  )

  const component =
    isMounted &&
    (behavior === 'popover' ? (
      <FloatingFocusManager context={context} restoreFocus={true}>
        {floatingContent}
      </FloatingFocusManager>
    ) : (
      floatingContent
    ))

  if (!enabled) {
    return children
  }

  // references:
  // - https://hub.accessible360.com/kb/articles/524#heading-for-automaticallyactivated-tooltips
  // - https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Roles/tooltip_role
  return (
    <>
      {typeof children === 'string' ? (
        <span
          aria-describedby={forceOpen || isOpen ? id : undefined}
          ref={refs.setReference}
          tabIndex={0}
          {...getReferenceProps()}
        >
          {children}
        </span>
      ) : (
        cloneElement(children, {
          ref: (node: ReferenceType) => {
            refs.setReference(node)
            cloneRef?.(node)
          },
          ...getReferenceProps({}),
          'aria-describedby': forceOpen || isOpen ? id : undefined,
        })
      )}
      {(forceOpen || isOpen) &&
        (portal ? (
          <FloatingPortal id={portalId}>{component}</FloatingPortal>
        ) : (
          component
        ))}
    </>
  )
}
